import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  X,
  Search,
  ChevronRight,
  Book,
  ExternalLink,
  HelpCircle,
} from 'lucide-react'
import {
  knowledgeBase,
  getArticlesForPage,
  searchArticles,
  KnowledgeArticle,
} from '../knowledge'

interface HelpModalProps {
  isOpen: boolean
  onClose: () => void
}

export function HelpModal({ isOpen, onClose }: HelpModalProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedArticle, setSelectedArticle] = useState<KnowledgeArticle | null>(null)

  // Get contextual articles based on current page
  const contextualArticles = getArticlesForPage(location.pathname)

  // Search results
  const searchResults = searchQuery.length > 1 ? searchArticles(searchQuery) : []

  // Determine which articles to show
  const displayArticles = searchQuery.length > 1
    ? searchResults
    : contextualArticles.length > 0
      ? contextualArticles
      : knowledgeBase.sections[0]?.articles || []

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
      setSelectedArticle(null)
    }
  }, [isOpen])

  // Get page title for context
  const getPageTitle = (path: string): string => {
    const titles: Record<string, string> = {
      '/': 'Dashboard',
      '/dashboard': 'Dashboard',
      '/controls': 'Controls',
      '/mappings': 'Mappings',
      '/evidence': 'Evidence',
      '/documents': 'Documents',
      '/tasks': 'Tasks',
      '/risks': 'Risks',
      '/kris': 'Key Risk Indicators',
      '/dark-web': 'Dark Web Monitoring',
      '/tprm': 'Vendor Risk Management',
      '/scanners': 'Security Scanning',
      '/assets': 'Assets',
      '/integrations': 'Integrations',
      '/users': 'Users',
      '/settings': 'Settings',
      '/help': 'Help',
    }
    return titles[path] || 'This Page'
  }

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-primary-600" />
            <h2 className="font-semibold text-gray-900">Help</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                onClose()
                navigate('/help')
              }}
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
            >
              Full Documentation
              <ExternalLink className="h-3 w-3" />
            </button>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search help articles..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                setSelectedArticle(null)
              }}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              autoFocus
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {selectedArticle ? (
            // Article View
            <div className="p-4">
              <button
                onClick={() => setSelectedArticle(null)}
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 mb-4"
              >
                <ChevronRight className="h-4 w-4 rotate-180" />
                Back to articles
              </button>

              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {selectedArticle.title}
              </h3>

              <div className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-li:text-gray-600 prose-strong:text-gray-900 prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded">
                <ArticleContent content={selectedArticle.content} />
              </div>
            </div>
          ) : (
            // Article List View
            <div className="p-4">
              {/* Context Label */}
              {!searchQuery && contextualArticles.length > 0 && (
                <div className="mb-3">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Help for {getPageTitle(location.pathname)}
                  </span>
                </div>
              )}

              {searchQuery && (
                <div className="mb-3">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{searchQuery}"
                  </span>
                </div>
              )}

              {!searchQuery && contextualArticles.length === 0 && (
                <div className="mb-3">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Getting Started
                  </span>
                </div>
              )}

              {/* Article List */}
              <div className="space-y-2">
                {displayArticles.length > 0 ? (
                  displayArticles.map((article) => (
                    <button
                      key={article.id}
                      onClick={() => setSelectedArticle(article)}
                      className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors group"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 group-hover:text-primary-700">
                            {article.title}
                          </h4>
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                            {article.summary}
                          </p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-primary-600 flex-shrink-0 mt-1" />
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Book className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No articles found</p>
                  </div>
                )}
              </div>

              {/* Browse All Sections */}
              {!searchQuery && (
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Browse All Topics
                  </span>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {knowledgeBase.sections.slice(0, 8).map((section) => (
                      <button
                        key={section.id}
                        onClick={() => {
                          if (section.articles.length > 0) {
                            setSelectedArticle(section.articles[0])
                          }
                        }}
                        className="text-left p-2 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <span className="text-sm text-gray-700">{section.title}</span>
                        <span className="text-xs text-gray-400 ml-1">
                          ({section.articles.length})
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

// Simple markdown-ish content renderer
function ArticleContent({ content }: { content: string }) {
  const lines = content.split('\n')
  const elements: JSX.Element[] = []
  let inList = false
  let listItems: string[] = []
  let inTable = false
  let tableRows: string[][] = []

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-1 my-3">
          {listItems.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      )
      listItems = []
    }
    inList = false
  }

  const flushTable = () => {
    if (tableRows.length > 0) {
      const headers = tableRows[0]
      const body = tableRows.slice(2) // Skip header separator row
      elements.push(
        <div key={`table-${elements.length}`} className="overflow-x-auto my-4">
          <table className="min-w-full text-sm border border-gray-200 rounded">
            <thead className="bg-gray-50">
              <tr>
                {headers.map((h, i) => (
                  <th key={i} className="px-3 py-2 text-left font-medium text-gray-700 border-b">
                    {h.trim()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  {row.map((cell, j) => (
                    <td key={j} className="px-3 py-2 border-b border-gray-100">
                      {cell.trim()}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
      tableRows = []
    }
    inTable = false
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Table detection
    if (line.startsWith('|')) {
      if (!inTable) {
        flushList()
        inTable = true
      }
      const cells = line.split('|').filter((c) => c.trim() !== '' && !c.match(/^[-:]+$/))
      if (cells.length > 0 && !line.match(/^\|[-:| ]+\|$/)) {
        tableRows.push(cells)
      } else if (line.match(/^\|[-:| ]+\|$/)) {
        tableRows.push([]) // Separator row marker
      }
      continue
    } else if (inTable) {
      flushTable()
    }

    // Headers
    if (line.startsWith('# ')) {
      flushList()
      elements.push(
        <h1 key={i} className="text-xl font-bold text-gray-900 mt-6 mb-3 first:mt-0">
          {line.slice(2)}
        </h1>
      )
    } else if (line.startsWith('## ')) {
      flushList()
      elements.push(
        <h2 key={i} className="text-lg font-semibold text-gray-900 mt-5 mb-2">
          {line.slice(3)}
        </h2>
      )
    } else if (line.startsWith('### ')) {
      flushList()
      elements.push(
        <h3 key={i} className="text-base font-semibold text-gray-900 mt-4 mb-2">
          {line.slice(4)}
        </h3>
      )
    }
    // List items
    else if (line.startsWith('- ') || line.startsWith('* ')) {
      inList = true
      listItems.push(line.slice(2))
    }
    // Numbered lists
    else if (line.match(/^\d+\.\s/)) {
      if (!inList) {
        flushList()
        inList = true
      }
      listItems.push(line.replace(/^\d+\.\s/, ''))
    }
    // Empty line
    else if (line.trim() === '') {
      flushList()
    }
    // Regular paragraph
    else if (line.trim()) {
      flushList()
      elements.push(
        <p key={i} className="text-gray-600 my-2">
          {formatInlineText(line)}
        </p>
      )
    }
  }

  flushList()
  flushTable()

  return <>{elements}</>
}

// Format inline markdown (bold, code, etc.)
function formatInlineText(text: string): React.ReactNode {
  const parts: React.ReactNode[] = []
  let remaining = text
  let key = 0

  while (remaining.length > 0) {
    // Bold
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/)
    // Code
    const codeMatch = remaining.match(/`(.+?)`/)

    if (boldMatch && (!codeMatch || boldMatch.index! < codeMatch.index!)) {
      const before = remaining.slice(0, boldMatch.index)
      if (before) parts.push(before)
      parts.push(<strong key={key++}>{boldMatch[1]}</strong>)
      remaining = remaining.slice(boldMatch.index! + boldMatch[0].length)
    } else if (codeMatch) {
      const before = remaining.slice(0, codeMatch.index)
      if (before) parts.push(before)
      parts.push(
        <code key={key++} className="bg-gray-100 px-1 py-0.5 rounded text-sm">
          {codeMatch[1]}
        </code>
      )
      remaining = remaining.slice(codeMatch.index! + codeMatch[0].length)
    } else {
      parts.push(remaining)
      break
    }
  }

  return parts.length > 0 ? parts : text
}

// Header Help Button component
export function HelpButton() {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        title="Help"
      >
        <HelpCircle className="h-5 w-5" />
      </button>
      <HelpModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  )
}
